import React from "react";
import { Typography, Box, Grid, TextField, Chip } from "@mui/material";
import AutoCompleteField from "../../../Forms/AutoCompleteField";
import { DatePicker } from "@mui/x-date-pickers";
import { sexOptions } from "../../../../utils/const";
import ResponsiveButton from "../../../Forms/ResponsiveButton";
import CloseIcon from "@mui/icons-material/Close";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
const fontSx = {
  fontSize: ["1rem", "1rem", "1rem", "1rem", "1.5rem", "1.5rem"],
};

const buttonStyles = {
  button: {
    background: "status.info",
    color: "white.main",
  },
};

const DatePickerStyle = {
  width: "100%",
  "& .MuiInputLabel-root": {
    fontSize: ["1rem", "1rem", ".7rem", ".7rem", "1rem", "1rem"],
    transform: {
      md: "matrix(1, 0, 0, 1, 0, 15)",
      lg: "matrix(1, 0, 0, 1, 0, 20)",
    },
  },
  "& .MuiInputLabel-shrink": {
    transform: {
      md: "matrix(0.75, 0, 0, 0.75, 0, -1.5)",
    },
  },
  "& .MuiInput-root": {
    mt: ["1rem", ".5rem", ".5rem", ".5rem", "1rem", "1rem"],
  },
  "& .MuiInput-input": {
    fontSize: ["1rem", ".66rem", ".66rem", ".66rem", "1rem", "1rem"],
  },
  "& .MuiInputAdornment-positionEnd": {
    mr: ["1rem", ".5rem", ".5rem", ".5rem", "1rem", "1rem"],
  },
  "& .MuiSvgIcon-root": {
    fontSize: ["1rem", "1rem", "1rem", "1rem", "1.5rem", "1.5rem"],
  },
};

const TextFieldStyle = {
  "& .MuiChip-root": {
    height: ["1.5rem", "1.5rem", "1.25rem", "1.25rem", "1.85rem", "1.85rem"],
    m: ["0rem", "0rem", ".15rem", ".25rem", ".25rem", ".25rem"],
  },
  "& .MuiChip-label": {
    fontSize: [".5rem", ".5rem", ".75rem", ".75rem", "1.05rem", "1.05rem"],
    px: [".5rem", ".5rem", ".5rem", ".5rem", ".60rem", ".60rem"],
  },
  "& .MuiChip-deleteIcon": {
    fontSize: [
      "1.5rem",
      "1.5rem",
      "1rem !important",
      "1rem !important",
      "1.5rem !important",
      "1.5rem !important",
    ],
  },
  "& .MuiFilledInput-input": {
    m: ["0rem", "0rem", "0rem", "0rem", ".1rem", ".1rem"],
    py: [".5rem", ".5rem", ".55rem", ".65rem", ".85rem", ".85rem"],
  },
  "& .MuiInputBase-input": {
    padding: "3 !important",
    height: ["1rem", "1rem", "1rem", "1rem", "1.5rem", "1.5rem"],
  },
  "& .MuiInputLabel-root": {
    fontSize: [".5rem", ".5rem", ".8rem", ".8rem", "1.15rem", "1.15rem"],
  },
  "& .MuiInputLabel-shrink": {
    transform: {
      md: "translate(12px, 3px) scale(0.75)",
      lg: "translate(12px, 7px) scale(0.75)",
    },
  },
  "& .MuiFilledInput-root": {
    pt: [".75rem", ".75rem", ".5rem", ".5rem", ".9rem", ".9rem"], // Reduce top padding
    minHeight: ["auto", "auto", "auto", "auto", "auto", "auto"], // Remove minimum height constraint
  },
  marginTop: [".5rem", ".5rem", ".75rem", ".75rem", "1rem", "1rem"],
};

const computeHelperText = (props, error, startDate, endDate) => {
  if (error) {
    if (startDate > endDate) {
      if (props?.label === "Start Datetime")
        return "Date should not be after maximal date";
      else return "Date should not be before minimal date";
    }
    if (props?.inputProps?.value?.length === "mm/dd/yyyy hh:mm".length) {
      return "Invalid Date Format";
    } else {
      return "mm/dd/yyyy hh:mm";
    }
  }
  return "";
};

const TrayFilterModal = ({
  generalFilters,
  updateGeneralFilters,
  options,
  clearGeneralFilters,
  handleClose,
}) => {
  const {
    algorithmGenusSpeciesOptions,
    contributedGenusSpeciesOptions,
    contributedPlatesOptions,
    trapTypesOptions,
    // deviceOptions,
    trapSiteOptions,
    usersOptions,
    tagsOption,
  } = options;
  const {
    // eslint-disable-next-line no-unused-vars
    deviceID,
    imagingStartDate,
    imagingEndDate,
    specimenCaptureStartDate,
    specimenCaptureEndDate,
    genusSpecies,
    plates,
    trapSitesFilter,
    trapTypesFilter,
    algorithmGenusSpeciesFilter,
    sexFilter,
    // eslint-disable-next-line no-unused-vars
    algorithmSexFilter,
    usersFilter,
    specimenTagFilter,
    trayIdFilter,
    specimenIdFilter,
  } = generalFilters;
  // const setDeviceID = (val) => updateGeneralFilters({ 'deviceID': val })
  const setImagingStartDate = (val) =>
    updateGeneralFilters({
      imagingStartDate: val,
    });
  const setImagingEndDate = (val) => {
    if (Object.prototype.toString.call(val) === "[object Date]") {
      val.setHours(23);
      val.setMinutes(59);
      val.setSeconds(59);
    }

    return updateGeneralFilters({ imagingEndDate: val });
  };
  const setSpecimenCaptureStartDate = (val) =>
    updateGeneralFilters({ specimenCaptureStartDate: val });
  const setSpecimenCaptureEndDate = (val) => {
    if (Object.prototype.toString.call(val) === "[object Date]") {
      val.setHours(23);
      val.setMinutes(59);
      val.setSeconds(59);
    }
    return updateGeneralFilters({ specimenCaptureEndDate: val });
  };
  const setGenusSpecies = (val) => updateGeneralFilters({ genusSpecies: val });
  const setPlates = (val) => updateGeneralFilters({ plates: val });
  const setTrapSitesFilter = (val) =>
    updateGeneralFilters({ trapSitesFilter: val });
  const setTrapTypesFilter = (val) =>
    updateGeneralFilters({ trapTypesFilter: val });
  const setAlgorithmGenusSpeciesFilter = (val) =>
    updateGeneralFilters({ algorithmGenusSpeciesFilter: val });
  const setUsersFilter = (val) => updateGeneralFilters({ usersFilter: val });
  const setSexFilter = (val) => updateGeneralFilters({ sexFilter: val });
  const setSpecimenTagFilter = (val) =>
    updateGeneralFilters({ specimenTagFilter: val });
  const setTrayIdFilter = (val) => updateGeneralFilters({ trayIdFilter: val });
  const setSpecimenIdFilter = (val) =>
    updateGeneralFilters({ specimenIdFilter: val });

  return (
    <Box sx={{ overflowY: "auto" }} aria-label="tray-filter-modal">
      <Grid sx={{ m: 2 }}>
        <Grid container rowSpacing={1} columnSpacing={{ xs: 1, sm: 2, md: 3 }}>
          <Grid size={1} />
          <Grid size={10}>
            <Typography sx={fontSx} className={" text-gray-600 text-center"}>
              {" "}
              Tray Filters{" "}
            </Typography>
          </Grid>
          <Grid size={1}>
            <Tooltip
              title={`Close`}
              sx={{
                float: "right",
              }}
            >
              <IconButton
                sx={{
                  color: "status.info",
                  padding: [
                    "0.35rem",
                    "0.35rem",
                    "0.35rem",
                    "0.5rem",
                    "0.5rem",
                    "0.5rem",
                  ],
                }}
                onClick={() => handleClose()}
              >
                <CloseIcon
                  sx={{
                    fontSize: [
                      "1rem",
                      "1rem",
                      ".90rem",
                      "1rem",
                      "1.75rem",
                      "1.75rem",
                    ],
                  }}
                />
              </IconButton>
            </Tooltip>
          </Grid>
          <Grid size={12}>
            <Typography
              sx={{
                fontSize: [
                  ".75rem",
                  ".75rem",
                  ".75rem",
                  ".75rem",
                  "1rem",
                  "1rem",
                ],
              }}
              className={"text-gray-800 mt-3"}
            >
              Imaging Date:
            </Typography>
          </Grid>
          <Grid size={{ md: 6, lg: 6 }}>
            <DatePicker
              autoOk
              style={{ maxWidth: "4rem" }}
              value={imagingStartDate}
              onChange={setImagingStartDate}
              inputFormat="MM/dd/yyyy"
              label="Start Date"
              mask="__/__/____"
              maxDate={imagingEndDate}
              showToolbar
              renderInput={(props) => (
                <TextField
                  data-testid="Imaging Start Date"
                  label="Start Date"
                  variant="standard"
                  sx={DatePickerStyle}
                  {...props}
                  helperText={computeHelperText(
                    props,
                    props.error,
                    imagingStartDate,
                    imagingEndDate
                  )}
                />
              )}
            />
          </Grid>
          <Grid size={{ md: 6, lg: 6 }}>
            <DatePicker
              style={{ maxWidth: "10rem" }}
              label="End Date"
              value={imagingEndDate}
              onChange={setImagingEndDate}
              inputFormat="MM/dd/yyyy"
              mask="__/__/____"
              minDate={imagingStartDate}
              showToolbar={true}
              renderInput={(props) => (
                <TextField
                  data-testid="Imaging End Date"
                  label="End Date"
                  variant="standard"
                  sx={DatePickerStyle}
                  {...props}
                  helperText={computeHelperText(
                    props,
                    props.error,
                    imagingStartDate,
                    imagingEndDate
                  )}
                />
              )}
            />
          </Grid>
          <Grid size={12}>
            <Typography
              sx={{
                fontSize: [
                  ".75rem",
                  ".75rem",
                  ".75rem",
                  ".75rem",
                  "1rem",
                  "1rem",
                ],
                display: "bolck",
              }}
              className={"text-gray-800 mt-3"}
            >
              Specimen Capture Date:
            </Typography>
          </Grid>
          <Grid size={{ md: 6, lg: 6 }}>
            <DatePicker
              autoOk
              style={{ maxWidth: "4rem" }}
              value={specimenCaptureStartDate}
              onChange={setSpecimenCaptureStartDate}
              inputFormat="MM/dd/yyyy"
              label="Start Date"
              mask="__/__/____"
              maxDate={specimenCaptureEndDate}
              showToolbar
              renderInput={(props) => (
                <TextField
                  data-testid="Specimen Capture Start Date"
                  label="Start Date"
                  variant="standard"
                  sx={DatePickerStyle}
                  {...props}
                  helperText={computeHelperText(
                    props,
                    props.error,
                    specimenCaptureStartDate,
                    specimenCaptureEndDate
                  )}
                  // style={{ paddingRight: 10 }}
                />
              )}
            />
          </Grid>
          <Grid size={{ md: 6, lg: 6 }}>
            <DatePicker
              style={{ maxWidth: "10rem" }}
              label="End Date"
              value={specimenCaptureEndDate}
              onChange={setSpecimenCaptureEndDate}
              inputFormat="MM/dd/yyyy"
              mask="__/__/____"
              minDate={specimenCaptureStartDate}
              showToolbar={true}
              renderInput={(props) => (
                <TextField
                  data-testid="Specimen Capture End Date"
                  label="End Date"
                  variant="standard"
                  sx={DatePickerStyle}
                  {...props}
                  helperText={computeHelperText(
                    props,
                    props.error,
                    specimenCaptureStartDate,
                    specimenCaptureEndDate
                  )}
                />
              )}
            />
          </Grid>
          <Grid size={{ sm: 12, lg: 12 }}>
            <TextField
              value={trayIdFilter}
              onChange={(e) => {
                setTrayIdFilter(e.target.value);
              }}
              variant="filled"
              label="Tray ID Filter"
              placeholder="Tray ID Filter"
              fullWidth
              sx={TextFieldStyle}
            />
          </Grid>
          <Grid size={{ sm: 12, lg: 12 }}>
            <TextField
              value={specimenIdFilter}
              onChange={(e) => {
                setSpecimenIdFilter(e.target.value);
              }}
              variant="filled"
              label="Specimen ID Filter"
              placeholder="Specimen ID Filter"
              fullWidth
              sx={TextFieldStyle}
            />
          </Grid>
          <Grid size={{ sm: 12, lg: 12 }}>
            <AutoCompleteField
              label={"Contributed Species Filter"}
              value={genusSpecies}
              disableCloseOnSelect
              onChange={(e, newValue) => {
                setGenusSpecies(newValue);
              }}
              getOptionLabel={(option) =>
                `${option.name?.genus} ${option.name?.species}`
              }
              multiple
              options={contributedGenusSpeciesOptions}
            />
          </Grid>
          <Grid size={{ sm: 12, lg: 12 }}>
            <AutoCompleteField
              label={"Algorithm Species Filter"}
              value={algorithmGenusSpeciesFilter}
              disableCloseOnSelect
              onChange={(e, newValue) => {
                setAlgorithmGenusSpeciesFilter(newValue);
              }}
              getOptionLabel={(option) =>
                `${option.name.genus} ${option.name.species}`
              }
              multiple
              options={algorithmGenusSpeciesOptions}
            />
          </Grid>
          <Grid size={{ sm: 12, lg: 12 }}>
            <AutoCompleteField
              label={"Plates Filter"}
              value={plates}
              disableCloseOnSelect
              onChange={(e, newValue) => {
                setPlates(newValue);
              }}
              getOptionLabel={(option) => option}
              multiple
              options={contributedPlatesOptions}
            />
          </Grid>
          <Grid size={{ sm: 12, lg: 12 }}>
            <AutoCompleteField
              label={"Contributed Sex Filter"}
              disableCloseOnSelect
              value={sexFilter}
              onChange={(e, newValue) => {
                setSexFilter(newValue);
              }}
              getOptionLabel={(option) => option}
              multiple
              options={sexOptions}
            />
          </Grid>
          <Grid size={{ sm: 12, lg: 12 }}>
            <AutoCompleteField
              label={"Collection Sites Filter"}
              disableCloseOnSelect
              value={trapSitesFilter}
              onChange={(e, newValue) => {
                setTrapSitesFilter(newValue);
              }}
              getOptionLabel={(option) => option.displayName}
              multiple
              options={trapSiteOptions}
            />
          </Grid>
          <Grid size={{ sm: 12, lg: 12 }}>
            <AutoCompleteField
              label={"Collection Methods Filter"}
              value={trapTypesFilter}
              disableCloseOnSelect
              onChange={(e, newValue) => {
                setTrapTypesFilter(newValue);
              }}
              getOptionLabel={(option) => option.displayName}
              multiple
              options={trapTypesOptions}
            />
          </Grid>
          <Grid size={{ sm: 12, lg: 12 }}>
            <AutoCompleteField
              label={"Users Filter"}
              value={usersFilter}
              disableCloseOnSelect
              onChange={(e, newValue) => {
                setUsersFilter(newValue);
              }}
              getOptionLabel={(option) => option}
              multiple
              options={usersOptions}
            />
          </Grid>
          <Grid size={{ sm: 12, lg: 12 }}>
            <AutoCompleteField
              label={"Specimens and Tray Tag Filter"}
              value={specimenTagFilter}
              disableCloseOnSelect
              onChange={(e, newValue) => {
                setSpecimenTagFilter(newValue);
              }}
              getOptionLabel={(option) => option}
              multiple
              freeSolo
              options={tagsOption}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    variant="outlined"
                    label={option}
                    {...getTagProps({ index })}
                  />
                ))
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="filled"
                  label="freeSolo"
                  placeholder="Favorites"
                />
              )}
            />
          </Grid>
          <Grid size={{ sm: 12, lg: 12 }}>
            <Box
              className={"flex"}
              sx={{
                mt: ["1.5rem", "1.5rem", ".75rem", "1.5rem", "1.5rem"],
                justifyContent: "space-around",
              }}
            >
              <ResponsiveButton
                onClick={(e) => {
                  e.stopPropagation();
                  clearGeneralFilters();
                }}
                sx={buttonStyles}
                id={"reset-data-download-filters"}
              >
                Reset
              </ResponsiveButton>
            </Box>
          </Grid>
        </Grid>
      </Grid>
    </Box>
  );
};

export default TrayFilterModal;
